/**
 * Discord avatar + display name helpers shared by `AppHeader`'s profile
 * trigger and `ProfilePopover`'s larger avatar slot.
 *
 * Both components historically inlined the same five computed values +
 * the on-error handler + the watcher that resets the failure flag whenever
 * the avatar URL changes. This composable wraps the contract so the two
 * stay in lock-step.
 *
 * Single source of truth for the Discord CDN URL pattern. The `size` query
 * string is per-call-site (64 for the header chip, 128 for the popover), so
 * we expose a `size` option that defaults to 64 — the most common value.
 */
import { computed, ref, watch, type ComputedRef, type Ref } from 'vue'

import { displayUsername, session } from '@/app/session'

export interface UseDiscordAvatarOptions {
  readonly size?: number
}

export interface UseDiscordAvatarReturn {
  readonly avatarUrl: ComputedRef<string | null>
  readonly avatarFailed: Ref<boolean>
  readonly onAvatarError: () => void
  readonly displayName: ComputedRef<string>
  readonly avatarInitial: ComputedRef<string>
}

/**
 * Discord CDN avatar URL for the current session; `null` when the session is
 * anonymous or no avatar hash is on file. The leading slash is omitted —
 * Discord serves the URL directly.
 */
export function useDiscordAvatar(
  options: UseDiscordAvatarOptions = {},
): UseDiscordAvatarReturn {
  const size = options.size ?? 64

  const avatarUrl = computed<string | null>(() => {
    const s = session.value
    if (!s || !s.avatar) return null
    // `discordUserId` is a string here — the backend serializes snowflakes as
    // JSON strings (see `snowflake_serde` in auth.rs) so the full 64-bit value
    // survives `JSON.parse` without 53-bit-precision loss. Direct
    // interpolation is enough; the string is already the right shape for
    // Discord's avatar URL.
    return `https://cdn.discordapp.com/avatars/${s.discordUserId}/${s.avatar}.png?size=${size}`
  })

  /**
   * Becomes `true` if the Discord CDN avatar `<img>` fires `error` (timeout,
   * blocked, 404, etc.) so the template can fall back to the colored initial.
   * Resets whenever `avatarUrl` changes so a transient CDN hiccup doesn't
   * permanently hide a working avatar across logins / guild switches.
   */
  const avatarFailed = ref<boolean>(false)

  watch(avatarUrl, () => {
    avatarFailed.value = false
  })

  function onAvatarError(): void {
    avatarFailed.value = true
  }

  /**
   * Display name for the trigger chip + popover. The backend pre-resolves
   * `global_name || username` into `Session::username`, which may still be
   * `CoolUser#1234` if the user never set a global display name. Strip the
   * legacy discriminator so the trigger and the popover agree on what to show.
   */
  const displayName = computed<string>(() =>
    displayUsername(session.value?.username ?? ''),
  )

  /** Single-character fallback for the avatar slot when the CDN image fails. */
  const avatarInitial = computed<string>(() => {
    const first = displayName.value.charAt(0)
    return first ? first.toUpperCase() : '?'
  })

  return {
    avatarUrl,
    avatarFailed,
    onAvatarError,
    displayName,
    avatarInitial,
  }
}
